import Raven from 'raven-js';
import CryptoJS from 'crypto-js';
import Vue from 'vue';
// import RavenVue from 'raven-js/plugins/vue';
import config from 'config';
import store from '@/vuex/index';
import versions from '@/config/config-version';
// import util, { sensitiveEmail } from '@/utils';
import { getH5Platform, isIOS } from 'utils/environment';
import { WHITE_LIST_URL_MAP } from '@/sub-tree/common/src/sentry/white-list-url-map';
import sentryUtil from '@/sub-tree/common/src/sentry/util';

const { iterationVersion, appVersion } = versions;
let AES;

let platform = getH5Platform();
let isDisableSentry = function () {
  let hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /192\.168\.\d+\.\d+/.test(hostname)) return true;
  const shouldReportData = sentryUtil.shouldReportData(store.getters.getCompanyConfigurations);
  return platform === 'cd' ? false : !shouldReportData;
};

const SENTRY_URL_MAP = {
  dev: 'http://92bde20f0a364853b439d97dbf18142b@192.168.10.150:9000/3',
  // 线上环境改为https
  prod: 'https://4449d839d81242f999c1ba197a86caf2@sentry-inspect.huilianyi.com/2',
  'prod-all': 'https://d754feb5b6e942eda0d737b5043d7c19@sentry-inspect.huilianyi.com/10',
  stage: 'https://8de29df8bdf44d158198e22f983f7c23@sentry-inspect.huilianyi.com/5', // stage 更换到测试服务器
  default: 'https://a5cb2425db97406f9654fc6ef805b8d1@sentry-inspect.huilianyi.com/6',
};

// 姓名脱敏
const sensitiveName = (name) => {
  if (!name) return name;
  let nameReg = /^(.).(.*)$/gim;
  return name.replace(nameReg, '$1*$2');
};
// 手机号脱敏
const sensitiveMobile = (mobile) => {
  if (!mobile) return mobile;
  let mobileReg = /^(.{3}).{4}(.*)$/gim;
  return mobile.replace(mobileReg, '$1****$2');
};

export const sensitiveEmail = (email) => {
  if (!email) return email;
  let emailAry = email.split('@'),
    emailTitle = emailAry[0];
  if (emailTitle) {
    let emailLen = emailTitle.length,
      emailLenHalf = Math.floor(emailLen / 2),
      emailLenStart = Math.floor((emailLen - emailLenHalf) / 2),
      emailLenEnd = emailLenStart + emailLenHalf,
      newEmailAry = [],
      pos = 0;
    while (pos < emailLen) {
      if (pos < emailLenStart) {
        newEmailAry.push(emailTitle[pos]);
      } else if (pos < emailLenEnd) {
        newEmailAry.push('*');
      } else {
        newEmailAry.push(emailTitle[pos]);
      }
      pos++;
    }
    emailAry[0] = newEmailAry.join('');
    return emailAry.join('@');
  }
  return email;
};

// 获取DSN
const getDsn = (env) => {
  return SENTRY_URL_MAP[env] || SENTRY_URL_MAP['default'];
};

Raven.setUserInfo = function () {
  if (!AES) {
    AES = CryptoJS.AES;
  }
  const CryptoJSKey = CryptoJS.enc.Utf8.parse('SsEdwXAUNtz5JyQH');
  const CryptoJSkeyIv = CryptoJS.enc.Utf8.parse('OVDNWKSuvdljEcpQ');
  const CryptoJSEnCodeMode = {
    iv: CryptoJSkeyIv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  };
  if (!Raven.userInfo) {
    let { state } = store,
      { myInfo } = state;
    if (myInfo) {
      let { fullName, email, mobile, avatar, userOID, tenantName, employeeID = '' } = myInfo;
      // mobile= '18611089856'
      let userInfo = {
        username: sensitiveName(fullName),
        email: sensitiveEmail(email),
        avatar: avatar,
        data: {
          encrypted: true,
          userOID: userOID,
          tenantName: tenantName,
          mobile: sensitiveMobile(mobile),
          unEncryptedEmail: email,
          unEncryptedMobile: mobile,
          unEncryptedUsername: fullName,
          employeeID: employeeID,
          encryptedEmail: AES.encrypt(email, CryptoJSKey, CryptoJSEnCodeMode).toString(),
          encryptedMobile: AES.encrypt(mobile, CryptoJSKey, CryptoJSEnCodeMode).toString(),
          encryptedEmailBak: AES.encrypt(email, CryptoJSKey, CryptoJSEnCodeMode).toString(),
          encryptedUsername: AES.encrypt(fullName, CryptoJSKey, CryptoJSEnCodeMode).toString(),
        },
      };
      Raven.setUserContext(userInfo);
      Raven.userInfo = userInfo;
    }
  }
};

const handleMapGet = (url, options = {}) => {
  const data = WHITE_LIST_URL_MAP.get(url);
  if (data && data.map) {
    return data.map(options);
  }
  return data;
};

export const RavenHttpError = (error, options = {}) => {
  if (isDisableSentry()) {
    return;
  }
  let { env } = config;
  let currentResponse = error.response || {},
    responseConfig = currentResponse.config || {},
    status = currentResponse.status || '',
    url = sentryUtil.getPureUrl(responseConfig.url || '');
  Raven.setUserInfo();
  const captureException = () => {
    const { headers: { currentPageKey, routesPath } = {} } = options;
    const localError = new Error(sentryUtil.getErrorMessage(currentResponse));
    let data = currentResponse && currentResponse.data;
    const exceptionCategory = error.vueErrorHandler
      ? 'FRONTEND_ERR'
      : sentryUtil.getExceptionCategory({ url: url, error: error });
    let hashString = '';
    let hashKey = '';
    if (exceptionCategory === 'FRONTEND_ERR') {
      hashString =
        Vue.prototype.COMMON.getValue(data, 'info.filename') ||
        Vue.prototype.COMMON.getValue(data, 'info.reason.stack') ||
        '';
      hashString = hashString.match(/\/(\S*).js/);
      hashKey = (hashString || [])[1] || '';
      const hashKeyArr = hashKey.split('/');
      hashKey = hashKeyArr.length ? hashKeyArr[hashKeyArr.length - 1] : '';
    }
    let hasKeyArr = exceptionCategory === 'FRONTEND_ERR' ? [hashKey] : [];
    const fingerprint = [`${url.toString()},${exceptionCategory}`, ...hasKeyArr];

    if (responseConfig.data) {
      delete responseConfig.data;
    }
    if (responseConfig.headers) {
      delete responseConfig.headers.Authorization;
    }
    responseConfig.url = url;
    let errorCode = data.bizErrorCode || data.errorCode;
    if (Array.isArray(data.validationErrors) && data.validationErrors.length > 0) {
      errorCode = data.validationErrors[0].code;
    }
    Raven.captureException(localError, {
      fingerprint: fingerprint,
      extra: Object.assign({}, currentResponse, { reqParams: options.data }),
      tags: {
        exceptionCategory: exceptionCategory,
        statusCode: status,
        errorCode: errorCode,
        currentPageKey: currentPageKey,
        routesPath: routesPath || (window.location && window.location.pathname),
      },
    });
  };

  Raven.setDSN(getDsn(env));
  return captureException(url);
};

export const InitSentry = () => {
  if (isDisableSentry()) {
    return;
  }
  let { appCode = '', env } = config;
  let sentryUrl = getDsn(env, true); //SENTRY_URL_MAP[`${env}-all`] || SENTRY_URL_MAP[env] || SENTRY_URL_MAP['default'];
  let options = {
    // ignoreErrors: [/ReferenceError:.*/],
    autoBreadcrumbs: {
      xhr: true, // XMLHttpRequest
      console: false, // console logging
      dom: false, // DOM interactions, i.e. clicks/typing
      location: true, // url changes, including pushState/popState
      sentry: false, // sentry events
    },
    tags: {
      appCode,
      platform,
      appVersion,
    },
    allowDuplicates: true,
    dataCallback(event) {
      // Modify the event here
      let { state } = store,
        { myInfo = {}, companyConfigurations = {} } = state;
      if (!myInfo) {
        myInfo = {};
      }
      let { tenantId } = companyConfigurations || {},
        { tenantName } = myInfo,
        { tags = {}, extra: { config: { url, vueErrorHandler } = {}, reqParams = {} } = {} } = event,
        localVIPEmails = ['joshua.shi@huilianyi.com'];
      url = sentryUtil.getPureUrl(url || '');
      let moduleName = vueErrorHandler
        ? sentryUtil.getModuleNameByRoutesPath(url, true)
        : sentryUtil.getModuleName(url);
      tags.tenantId = tenantId;
      tags.tenantName = tenantName;
      tags.iterationVersion = iterationVersion;
      tags.hostname = platform === 'cd' ? (isIOS() ? 'ios原生' : 'android原生') : location.hostname;
      tags.isVIP = false || myInfo.isVIP || localVIPEmails.includes(myInfo.email);
      let hashString = '';
      let hashKey = '';
      if (tags.exceptionCategory === 'FRONTEND_ERR') {
        hashString =
          Vue.prototype.COMMON.getValue(reqParams, 'info.filename') ||
          Vue.prototype.COMMON.getValue(reqParams, 'info.reason.stack') ||
          '';
        hashString = hashString.match(/\/(\S*).js/);
        hashKey = (hashString || [])[1] || '';
        const hashKeyArr = hashKey.split('/');
        hashKey = hashKeyArr.length ? hashKeyArr[hashKeyArr.length - 1] : '';
        tags.isStaticResource = !hashKey.includes('helios');
      }
      let hasKeyArr = vueErrorHandler ? [hashKey] : [];
      let transaction = [moduleName, url, tags.exceptionCategory, ...hasKeyArr].join('-');

      if (WHITE_LIST_URL_MAP.has(url)) {
        const urlMap = handleMapGet(url, reqParams);
        const { moduleAction } = urlMap;
        transaction = [moduleName, moduleAction, url, tags.exceptionCategory, ...hasKeyArr].join('-');
      }
      event.transaction = transaction;
      return event;
    },
  };
  Raven.config(sentryUrl, options);
  Vue.prototype.$RavenHttpError = RavenHttpError;
  // 暂时只监听审批接口
  // .addPlugin(RavenVue, Vue)
  // .install();
};
